import React, { createContext, useContext, useState, useEffect } from 'react';

const FontAccessibilityContext = createContext();

export const useFontAccessibility = () => {
  const context = useContext(FontAccessibilityContext);
  if (!context) {
    throw new Error('useFontAccessibility must be used within FontAccessibilityProvider');
  }
  return context;
};

export const FontAccessibilityProvider = ({ children }) => {
  const [fontSize, setFontSize] = useState('medium');

  // Apply font size to main-content element
const applyFontSize = (size) => {
  const htmlElement = document.documentElement;
  
  // Remove all font size classes from html element
  htmlElement.classList.remove(
    'font-size-small', 
    'font-size-medium', 
    'font-size-large', 
    'font-size-extra-large'
  );
  
  // Add the selected font size class to html element
  htmlElement.classList.add(`font-size-${size}`);
};

  // Load saved preference and apply on mount
  useEffect(() => {
    const savedFontSize = localStorage.getItem('preferredFontSize');
    if (savedFontSize && ['small', 'medium', 'large', 'extra-large'].includes(savedFontSize)) {
      setFontSize(savedFontSize);
    }
  }, []);

  // Apply font size whenever it changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      applyFontSize(fontSize);
    });

    // Also try again after a short delay as fallback
    const timeoutId = setTimeout(() => {
      applyFontSize(fontSize);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [fontSize]);

  const changeFontSize = (newSize) => {
    setFontSize(newSize);
    localStorage.setItem('preferredFontSize', newSize);
  };

  const increaseFontSize = () => {
    const sizes = ['small', 'medium', 'large', 'extra-large'];
    const currentIndex = sizes.indexOf(fontSize);
    if (currentIndex < sizes.length - 1) {
      changeFontSize(sizes[currentIndex + 1]);
    }
  };

  const decreaseFontSize = () => {
    const sizes = ['small', 'medium', 'large', 'extra-large'];
    const currentIndex = sizes.indexOf(fontSize);
    if (currentIndex > 0) {
      changeFontSize(sizes[currentIndex - 1]);
    }
  };

  const resetFontSize = () => {
    changeFontSize('medium');
  };

  return (
    <FontAccessibilityContext.Provider
      value={{
        fontSize,
        changeFontSize,
        increaseFontSize,
        decreaseFontSize,
        resetFontSize,
      }}
    >
      {children}
    </FontAccessibilityContext.Provider>
  );
};